/* eslint-disable import/extensions */
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  OfferingType,
  TermsType,
  SecurityType as SecurityTypeProps,
} from 'typings/markets/api';
import FavoriteIcon from '@material-ui/icons/Favorite';
import {
  darken,
  Grid,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'next-i18next';
import { DEFAULT_OFFER_COVER_IMAGES } from '@/constants';
import { toggleAddWatchlist } from '@/features/markets/api/slice';
import { AppDispatch } from '@/store/store';
import useKFormatter from '@/hooks/useKFormatter';
import useMoneyFormat from '@/hooks/useMoneyFormat';
import Timeline2 from '@/components/offering-card/Timeline2';
import DaysRemaining from '../../components/DaysRemaining';
import Typography from '../../components/Typography';
import Button from '../../components/Button';
import TimeAgo from '../../components/TimeAgo';
import SecurityCard from './SecurityCard';

interface StyleProps {
  withMarginTop: boolean;
  bgImg: string;
}

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    marginTop: (props: StyleProps) => (props.withMarginTop ? theme.spacing(3) : 0),
  },
  icon: {
    [theme.breakpoints.down('xs')]: {
      width: '60px',
    },
    width: '64px',
    height: '64px',
    backgroundColor: theme.palette.primary.main,
    marginRight: theme.spacing(2),
    borderRadius: '4px',
  },
  companyTitle: {
    flexGrow: 2,
  },
  companyTitleMobile: {
    flexGrow: 2,
    fontWeight: 500,
  },
  date: {
    display: 'flex',
    alignItems: 'center',
    height: '32px',
    backgroundColor: theme.palette.primary.superlight,
    borderRadius: '8px',
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    [theme.breakpoints.up('md')]: {
      marginLeft: theme.spacing(1),
    },
  },
  contentAll: {
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(1.5),
      justifyContent: 'space-between',
      height: '100%',
      // width: '100%',
    },
    display: 'flex',
    flexDirection: 'column',
  },
  contentRows: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(1.5),
  },
  mobilefullWidth: {
    [theme.breakpoints.down('sm')]: {
      position: 'relative',
      height: '200px',
    },
  },
  image: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'space-between',
    height: '463px',
    borderRadius: '4px',
    [theme.breakpoints.up('lg')]: {
      minWidth: '800px',
    },
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
      bottom: 0,
      top: 0,
      left: -16,
      right: -16,
      maxHeight: '200px',
      borderRadius: 0,
    },
    marginTop: theme.spacing(0.2),
    backgroundImage: (props: StyleProps) => `url(${props.bgImg})`,
    backgroundColor: (props: StyleProps) => (props.bgImg ? 'transparent' : theme.palette.primary.extralight),
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  },
  oversubscribed: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.positive.main,
    height: '50px',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
  },
  oversubscribedText: {
    color: theme.palette.greyScale.superlight,
  },
  bottomImage: {
    display: 'flex',
    justifyContent: 'space-between',
    flexGrow: 1,
    alignItems: 'flex-end',
  },
  category: {
    backgroundColor: theme.palette.primary.extralight,
    borderRadius: '4px',
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    padding: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
    marginLeft: theme.spacing(1.5),
  },
  platform: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '4px',
    backgroundColor: theme.palette.greyScale.extralight,
    maxHeight: '64px',
    maxWidth: '120px',
    marginBottom: theme.spacing(1.5),
    marginRight: theme.spacing(1.5),
  },
  copy: {
    '& >a': {
      textDecoration: 'none',
      color: darken(theme.palette.primary.main, 0.2),
      fontWeight: theme.typography.fontWeightBold,
    },
    fontStyle: 'italic',
  },
  rows: {
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(1),
    },
    [theme.breakpoints.up('md')]: {
      marginLeft: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
    },
  },
  green: {
    color: theme.palette.positive.main,
  },
  favoriteIconToggled: {
    color: theme.palette.negative.main,
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(1.5),
    },
  },
  overflow: {
    // overflowY: 'auto',
    height: '100%',
  },
  firstRow: {
    display: 'flex',
    justifyContent: 'space-between',
  },
}));

interface PropsDetails {
  category:
  | 'trade&services'
  | 'lifeSciences'
  | 'realEstate&construction'
  | 'energy&transportation'
  | 'technology'
  | 'finance'
  | 'manufacturing';
  platformLogo: string | null;
  companyName: string;
  hasUserSubscribed: boolean;
  raised: number | null;
  fundingGoalMin: number | null;
  fundingGoalMax: number | null;
  minTicket: number | null;
  investorsCount: number | null;
  daysRemaining: number | null;
  daysSinceStartdate: number | null;
  offerImage: string | null;
  withMarginTop: boolean;
  startDate: string;
  endDate: string | null;
  updatedAt: string;
  status: string;
  country: string;
  terms: TermsType | null;
  securityType: SecurityTypeProps | null;
  currency: string;
  regulation: string;
  linkToInvest: string;
  platformName: string;
  fullWidth: boolean;
  description: string;
  onToggleFavorites: (arg0: React.MouseEvent<HTMLButtonElement>) => void;
  showFav: boolean;
}

export const Summary = ({
  withMarginTop,
  category,
  platformLogo,
  companyName,
  hasUserSubscribed,
  raised,
  fundingGoalMin,
  fundingGoalMax,
  minTicket,
  investorsCount,
  daysRemaining,
  daysSinceStartdate,
  offerImage,
  startDate,
  endDate,
  updatedAt,
  status,
  securityType,
  terms,
  summaryTerms,
  currency,
  country,
  regulation,
  linkToInvest,
  platformName,
  fullWidth,
  description,
  logo,
  onToggleFavorites,
  showFav,
}: PropsDetails) => {
  const { t, i18n } = useTranslation('markets');
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.down('md'));
  const classes = useStyles({
    bgImg: offerImage || DEFAULT_OFFER_COVER_IMAGES.xs,
    withMarginTop,
  });
  const overMin = 100 * ((raised || 0) / (fundingGoalMin || 1)) > 100;
  const overMax = 100 * ((raised || 0) / (fundingGoalMax || 1)) > 100;

  return (
    <>
      <Grid container>
        {/* HEADER */}
        <Grid
          item
          className={classes.header}
          style={{
            marginBottom: theme.spacing(2),
            width: '100%',
          }}
        >
          {logo && (
            <img
              src="https://i.pinimg.com/originals/1f/fe/63/1ffe63f66945edc6aa8765fb76f72f0d.png"
              alt="company icon"
              className={classes.icon}
            />
          )}
          <div
            style={
              logo
                ? {
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  flexGrow: 1,
                }
                : {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexGrow: 1,
                }
            }
          >
            <div>
              <Typography
                variant="header"
                className={
                  md ? classes.companyTitleMobile : classes.companyTitle
                }
                style={{ flexGrow: 1 }}
              >
                {companyName}
              </Typography>
              {description !== 'None' && <Typography variant="base2">{description}</Typography>}
            </div>
            {showFav
            && (
            <IconButton onClick={onToggleFavorites}>
              <FavoriteIcon
                style={{ minHeight: '40px', minWidth: '40px' }}
                className={
                  hasUserSubscribed ? classes.favoriteIconToggled : undefined!
                }
              />
            </IconButton>
            )}
          </div>
        </Grid>

        {/* CHIPS */}
        <Grid item container style={{ marginBottom: theme.spacing(1) }}>
          <DaysRemaining
            status={status}
            days_remaining={daysRemaining}
            days_since_startdate={daysSinceStartdate}
            end_date={endDate}
          />
          {!(status === 'EXPIRED') && (
            <div className={classes.date}>
              <Typography variant={md ? 'small2bold' : 'secondary2bold'}>
                {t('markets.detailView.summary.start').toUpperCase()}
              </Typography>
              <Typography
                variant={md ? 'small2medium' : 'secondary2medium'}
                style={{ marginLeft: theme.spacing(0.5) }}
              >
                {new Date(startDate as string).toLocaleDateString(
                  i18n.language,
                  { year: 'numeric', month: 'long', day: 'numeric' },
                )}
              </Typography>
            </div>
          )}
        </Grid>

        {/* IMAGE + CARD INFORMATION */}
        <Grid container style={{ display: 'flex' }}>
          {/* IMAGE */}
          <Grid item xs={12} md={8} className={classes.mobilefullWidth}>
            <div className={classes.image}>
              <div className={classes.bottomImage}>
                {category ? (
                  <div className={classes.category}>
                    <Typography
                      variant={md ? 'small2bold' : 'base2bold'}
                      color="primary"
                    >
                      {t(`markets.filters.sectors.${category}`).toLowerCase()}
                    </Typography>
                  </div>
                ) : (
                  <div style={{ backgroundColor: 'transparent' }} />
                )}
                <div className={classes.platform}>
                  <img
                    src={platformLogo || ''}
                    alt="platform logo"
                    height="80%"
                    width="80%"
                  />
                </div>
              </div>
              {/* <Grid item>
              <Typography variant="caption" className={classes.copy}>
                Photo by
                {' '}
                <a href="https://unsplash.com//@bitu2104?utm_source=wixdom&utm_medium=referral">Tuyen Vo</a>
                {' '}
                on
                {' '}
                <a href="https://unsplash.com/?utm_source=wixdom&utm_medium=referral">Unsplash</a>
              </Typography>
            </Grid> */}
            </div>
          </Grid>

          {/* CARD INFORMATION */}
          <Grid item xs={12} md={4} className={classes.contentAll}>
            <Grid container className={classes.contentRows}>
              {/* TOTAL RAISED */}
              <Grid item className={classes.rows}>
                <div className={classes.firstRow}>
                  <Typography variant="base2">
                    {t('markets.detailView.summary.totalRaised')}
                  </Typography>
                  <Typography
                    variant="small2medium"
                    style={{ color: theme.palette.greyScale.dark }}
                  >
                    {t('markets.detailView.summary.updated')}
                    <TimeAgo updatedAt={updatedAt} />
                  </Typography>
                </div>
                <Typography variant="header">
                  {useMoneyFormat(
                    raised,
                    'en-US',
                    currency,
                    'currency',
                    'symbol',
                    0,
                    2,
                  )}
                </Typography>
                <div
                  style={{
                    marginTop: '-46px',
                    marginBottom: '-20px',
                    // paddingRight: theme.spacing(1),
                  }}
                >
                  <Timeline2
                    value={raised || 0}
                    goal={fundingGoalMax || 1}
                    fundingGoalMin={fundingGoalMin || 0}
                  />
                </div>
              </Grid>

              {/* FUNDING GOAL - MINIMUM TICKET */}
              <Grid className={classes.rows} style={{ display: 'flex' }}>
                <div style={{ flexBasis: '50%' }}>
                  <Typography variant="base2">
                    {t('markets.detailView.summary.fundingGoal')}
                  </Typography>
                  <Typography
                    variant="subheader"
                  >
                    <span
                      className={
                raised && 100 * (raised / (fundingGoalMin || 1)) > 100
                  ? classes.green
                  : undefined!
              }
                    >
                      $
                      {useKFormatter(fundingGoalMin || 0)}
                    </span>
                    <span
                      className={
                raised && 100 * (raised / (fundingGoalMax || 1)) >= 100
                  ? classes.green
                  : undefined!
              }
                    >
                      - $
                      {useKFormatter(fundingGoalMax || 0)}
                      {' '}
                    </span>
                  </Typography>
                </div>
                <div>
                  <Typography variant="base2">
                    {t('markets.detailView.summary.minimumTicket')}
                  </Typography>
                  <Typography variant="subheader">
                    {minTicket! ? `$ ${useKFormatter(minTicket!)}`
                      : undefined }
                  </Typography>
                </div>
              </Grid>

              {/* INVESTORS - REGULATION */}
              <div className={classes.rows} style={{ display: 'flex' }}>
                <div style={{ flexBasis: '50%' }}>
                  <Typography variant="base2">
                    {t('markets.detailView.summary.investors')}
                  </Typography>
                  <Typography variant="subheader">{investorsCount}</Typography>
                </div>
                <div>
                  <Typography variant="base2">
                    {t('markets.detailView.summary.regulation')}
                  </Typography>
                  <Typography variant="subheader">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {!md && (
                        <img
                          src={`/country_flags/${country.toLowerCase()}.svg`}
                          width="30px"
                          height="20px"
                          alt="County Flag"
                        />
                      )}
                      <div style={{ paddingLeft: '2px' }}>
                        {regulation}
                      </div>
                    </div>
                  </Typography>
                </div>
              </div>

              {/* SECURITY TYPE */}
              <Grid
                item
                style={{
                  marginTop: theme.spacing(1),
                }}
              >
                {(summaryTerms && summaryTerms.name !== undefined) && (
                  <SecurityCard
                    term={summaryTerms}
                    name={summaryTerms.name}
                    currency={currency}
                  />
                )}
              </Grid>
            </Grid>

            {/* BUTTON */}
            {linkToInvest
             && (
             <Grid container className={classes.buttons}>
               <Grid item>
                 <a
                   href={linkToInvest}
                   target="_blank"
                   rel="noreferrer"
                   style={{ textDecoration: 'none' }}
                 >
                   <Button
                     variant="contained"
                     fullWidth
                     text={t('markets.detailView.viewOn', {
                       platform: platformName,
                     })}
                   />
                 </a>
               </Grid>
             </Grid>
             )}

          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

interface Props {
  offering: OfferingType;
  withMarginTop: boolean;
  fullWidth: boolean;
  showFav: boolean
}
const defaultProps = {};

const SummaryController = ({
  offering, withMarginTop, fullWidth, showFav,
}: Props) => {
  const dispatch = useDispatch() as AppDispatch;
  const classes = useStyles({
    bgImg: offering.images.offer_image.lg || DEFAULT_OFFER_COVER_IMAGES.lg,
    withMarginTop,
  });
  const onToggleFavorites = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dispatch(toggleAddWatchlist({ slug: offering.slug }));
  };
  return (
    <Grid container style={{ width: '100%' }}>
      <div className={classes.overflow}>
        <Summary
          raised={offering.raised}
          offerImage={offering.images.offer_image.lg}
          fundingGoalMin={offering.summary?.funding_goal_min}
          fundingGoalMax={offering.summary?.funding_goal_max}
          minTicket={offering.ticket_amount_minimum}
          investorsCount={offering.investors_count}
          startDate={offering.start_date}
          endDate={offering.summary?.end_date}
          daysRemaining={offering.days_remaining}
          daysSinceStartdate={offering.days_since_startdate}
          updatedAt={offering.updated_at}
          category={offering.industry_category[0]}
          platformLogo={offering.platform.logo}
          companyName={offering.company_name}
          status={offering.status}
          securityType={offering.security_type}
          hasUserSubscribed={offering.has_user_subscribed}
          country={offering.country}
          terms={offering.terms?.standard}
          summaryTerms={offering?.summary?.terms}
          currency={offering.currency}
          regulation={offering.summary?.regulation_type}
          linkToInvest={offering.summary?.offering_link}
          platformName={offering.platform.name}
          withMarginTop={withMarginTop}
          fullWidth={fullWidth}
          description={offering.summary?.tagline}
          logo={offering.images?.logo.md}
          onToggleFavorites={onToggleFavorites}
          showFav={showFav}
        />
      </div>
    </Grid>
  );
};

SummaryController.defaultProps = (defaultProps as unknown) as Partial<Props>;

export default SummaryController;
